import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import api from "../api/axiosConfig";
import { useAuth } from "../context/AuthContext";
import { POLISH_CITY_OPTIONS } from "../data/polishCities";
import { useI18n } from "../i18n";
import "./OnboardingModal.css";

const MIN_AGE = 18;
const MAX_AGE = 120;
const PASSWORD_MIN_LENGTH = 6;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const FIELD_ORDER = ["email", "password", "username", "age", "gender", "city", "acceptedTerms", "confirmedAge18Plus"];

function OnboardingModal() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useI18n();
  const formRef = useRef(null);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    username: "",
    age: "",
    gender: "male",
    country: "Poland",
    city: "",
    acceptedTerms: false,
    confirmedAge18Plus: false
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    let mounted = true;

    api
      .get("/api/users/public/showcase-photos?count=6")
      .then((response) => {
        if (!mounted) return;
        const urls = (response.data || []).map((item) => item.photoUrl).filter(Boolean);
        setPhotos(urls);
      })
      .catch(() => {});

    return () => {
      mounted = false;
    };
  }, []);

  function handleChange(event) {
    const { name, value, type, checked } = event.target;

    setFormData((previousData) => ({
      ...previousData,
      [name]: type === "checkbox" ? checked : value
    }));

    setFieldErrors((previous) => {
      if (!previous[name]) return previous;
      const next = { ...previous };
      delete next[name];
      return next;
    });
  }

  function isValidAge(ageValue) {
    const numericAge = Number(ageValue);
    return Number.isInteger(numericAge) && numericAge >= MIN_AGE && numericAge <= MAX_AGE;
  }

  function validate() {
    const errors = {};

    if (!formData.email.trim()) {
      errors.email = t("registration.validationEmailRequired");
    } else if (!EMAIL_PATTERN.test(formData.email.trim())) {
      errors.email = t("registration.validationEmail");
    }

    if (!formData.password) {
      errors.password = t("registration.validationPasswordRequired");
    } else if (formData.password.length < PASSWORD_MIN_LENGTH) {
      errors.password = t("registration.validationPasswordLength", { length: PASSWORD_MIN_LENGTH });
    }

    if (!formData.username.trim()) errors.username = t("registration.validationNickname");
    if (!isValidAge(formData.age)) errors.age = t("registration.validationAge");
    if (!formData.gender) errors.gender = t("registration.validationGender");
    if (!formData.city.trim()) errors.city = t("registration.validationCity");
    if (!formData.acceptedTerms) errors.acceptedTerms = t("registration.validationTerms");
    if (!formData.confirmedAge18Plus) errors.confirmedAge18Plus = t("registration.validationAdult");

    return errors;
  }

  function showErrors(errors) {
    setFieldErrors(errors);

    const firstField = FIELD_ORDER.find((name) => errors[name]);
    const element = firstField && formRef.current?.querySelector(`[name="${firstField}"]`);

    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      if (typeof element.focus === "function") {
        element.focus({ preventScroll: true });
      }
    }
  }

  async function emailAlreadyExists() {
    try {
      const response = await axios.get("/api/auth/check-email", {
        params: { email: formData.email.trim() }
      });
      return Boolean(response.data?.exists);
    } catch (err) {
      // If the check itself fails, the server still rejects duplicates on submit.
      return false;
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");

    const errors = validate();

    if (Object.keys(errors).length > 0) {
      showErrors(errors);
      return;
    }

    setSubmitting(true);

    try {
      if (await emailAlreadyExists()) {
        showErrors({ email: t("registration.emailExists") });
        setSubmitting(false);
        return;
      }

      await axios.post("/api/auth/register", {
        username: formData.username.trim(),
        email: formData.email.trim(),
        password: formData.password,
        age: Number(formData.age),
        gender: formData.gender,
        country: formData.country.trim(),
        city: formData.city.trim(),
        bio: "",
        acceptedTerms: formData.acceptedTerms,
        confirmedAge18Plus: formData.confirmedAge18Plus
      });
    } catch (err) {
      console.error("Registration error:", err);
      setError(
        typeof err.response?.data === "string" && err.response.data
          ? err.response.data
          : t("registration.registrationFailed")
      );
      setSubmitting(false);
      return;
    }

    try {
      const loginResponse = await axios.post("/api/auth/login", {
        email: formData.email.trim(),
        password: formData.password
      });

      login(loginResponse.data);
      navigate("/browse");
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      });
    } catch (err) {
      console.error("Auto-login after registration failed:", err);
      navigate("/login", { state: { message: t("registration.accountCreated") } });
    }
  }

  function renderAgeOptions() {
    return Array.from({ length: MAX_AGE - MIN_AGE + 1 }, (_, index) => {
      const age = MIN_AGE + index;
      return (
        <option key={age} value={age}>
          {age}
        </option>
      );
    });
  }

  function renderFieldError(name) {
    return fieldErrors[name] ? (
      <span className="onboarding-field-error" role="alert">
        {fieldErrors[name]}
      </span>
    ) : null;
  }

  function fieldClass(name) {
    return `onboarding-field${fieldErrors[name] ? " has-error" : ""}`;
  }

  const consentReady = formData.acceptedTerms && formData.confirmedAge18Plus;

  return (
    <section
      className="onboarding-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-title"
    >
      <div className="onboarding-modal-header">
        <Link to="/" className="onboarding-brand" aria-label="TajneMysli home">
          <span>♥</span>
          <strong>TajneMysli</strong>
        </Link>

        <Link to="/login" className="onboarding-login-link">
          {t("registration.login")}
        </Link>
      </div>

      <div className="onboarding-copy">
        <h1 id="onboarding-title">{t("registration.welcomeTitle")}</h1>
        <p>{t("registration.welcomeDescription")}</p>
      </div>

      {photos.length > 0 && (
        <div className="onboarding-showcase">
          <p className="onboarding-showcase-caption">{t("registration.showcaseCaption")}</p>
          <div className="onboarding-showcase-strip">
            {photos.map((url) => (
              <img key={url} src={url} alt="" loading="lazy" />
            ))}
          </div>
        </div>
      )}

      {error && <div className="onboarding-message onboarding-error">{error}</div>}

      <form onSubmit={handleSubmit} className="onboarding-form" ref={formRef} noValidate>
        <div className={fieldClass("email")}>
          <label htmlFor="email">
            {t("registration.email")} <span className="onboarding-required-mark" aria-hidden="true">*</span>
          </label>
          <input
            id="email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder={t("registration.enterEmail")}
            autoComplete="email"
            inputMode="email"
            required
          />
          {renderFieldError("email")}
        </div>

        <div className={fieldClass("password")}>
          <label htmlFor="password">
            {t("registration.password")} <span className="onboarding-required-mark" aria-hidden="true">*</span>
          </label>
          <div className="onboarding-password-input-wrap">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={t("registration.passwordHint")}
              autoComplete="new-password"
              minLength={PASSWORD_MIN_LENGTH}
              required
            />
            <button
              type="button"
              className="onboarding-password-toggle"
              onClick={() => setShowPassword((visible) => !visible)}
              aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
              title={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                {showPassword ? (
                  <>
                    <path d="M3 3l18 18" />
                    <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
                    <path d="M9.9 5.2A10.8 10.8 0 0 1 12 5c5.2 0 8.6 4.3 9.7 7a16 16 0 0 1-3.1 4.1" />
                    <path d="M6.2 6.2C3.9 7.8 2.6 10 2.3 12c.5 1.2 1.6 2.9 3.4 4.3A10.6 10.6 0 0 0 12 19c1 0 2-.2 2.9-.5" />
                  </>
                ) : (
                  <>
                    <path d="M2.3 12C3.4 9.2 6.8 5 12 5s8.6 4.2 9.7 7c-1.1 2.8-4.5 7-9.7 7s-8.6-4.2-9.7-7Z" />
                    <circle cx="12" cy="12" r="2.5" />
                  </>
                )}
              </svg>
            </button>
          </div>
          {renderFieldError("password")}
        </div>

        <div className={fieldClass("username")}>
          <label htmlFor="username">
            {t("registration.nickname")} <span className="onboarding-required-mark" aria-hidden="true">*</span>
          </label>
          <input
            id="username"
            type="text"
            name="username"
            value={formData.username}
            onChange={handleChange}
            placeholder={t("registration.enterNickname")}
            autoComplete="nickname"
            required
          />
          {renderFieldError("username")}
        </div>

        <div className="onboarding-two-columns">
          <div className={fieldClass("age")}>
            <label htmlFor="age">
              {t("registration.age")} <span className="onboarding-required-mark" aria-hidden="true">*</span>
            </label>
            <select id="age" name="age" value={formData.age} onChange={handleChange} required>
              <option value="">{t("registration.selectAge")}</option>
              {renderAgeOptions()}
            </select>
            {renderFieldError("age")}
          </div>

          <div className={fieldClass("gender")}>
            <label htmlFor="gender">
              {t("registration.gender")} <span className="onboarding-required-mark" aria-hidden="true">*</span>
            </label>
            <select id="gender" name="gender" value={formData.gender} onChange={handleChange} required>
              <option value="male">{t("registration.male")}</option>
              <option value="female">{t("registration.female")}</option>
              <option value="other">{t("registration.other")}</option>
            </select>
            {renderFieldError("gender")}
          </div>
        </div>

        <div className={fieldClass("city")}>
          <label htmlFor="city">
            {t("registration.city")} <span className="onboarding-required-mark" aria-hidden="true">*</span>
          </label>
          <select
            id="city"
            name="city"
            value={formData.city}
            onChange={handleChange}
            autoComplete="address-level2"
            required
          >
            <option value="">{t("registration.selectCity")}</option>
            {POLISH_CITY_OPTIONS.map(({ label, value }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          {renderFieldError("city")}
        </div>

        <div className="onboarding-consent-box">
          <label className="onboarding-checkbox-label">
            <input
              type="checkbox"
              name="acceptedTerms"
              checked={formData.acceptedTerms}
              onChange={handleChange}
            />
            <span>
              {t("registration.agreeTo")}{" "}
              <Link to="/terms" target="_blank" rel="noreferrer">
                {t("registration.terms")}
              </Link>
              ,{" "}
              <Link to="/service-rules" target="_blank" rel="noreferrer">
                {t("registration.serviceRules")}
              </Link>
              , {t("registration.and")}{" "}
              <Link to="/privacy" target="_blank" rel="noreferrer">
                {t("registration.privacyPolicy")}
              </Link>
              .
            </span>
          </label>
          {renderFieldError("acceptedTerms")}

          <label className="onboarding-checkbox-label">
            <input
              type="checkbox"
              name="confirmedAge18Plus"
              checked={formData.confirmedAge18Plus}
              onChange={handleChange}
            />
            <span>{t("registration.confirmAdult")}</span>
          </label>
          {renderFieldError("confirmedAge18Plus")}
        </div>

        <div className="onboarding-submit-bar">
          <button
            type="submit"
            className={
              consentReady
                ? "onboarding-primary-button"
                : "onboarding-primary-button onboarding-primary-button-inactive"
            }
            disabled={submitting}
          >
            {submitting ? t("registration.creatingAccount") : t("registration.createAccount")}
          </button>
        </div>
      </form>

      <p className="onboarding-footer-text">
        {t("registration.alreadyHaveAccount")}{" "}
        <Link to="/login">{t("registration.loginInstead")}</Link>
      </p>
    </section>
  );
}

export default OnboardingModal;
